import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLines, EASE } from "@/lib/motion";
import { ClientLogos } from "@/components/site/ClientLogos";
import { useBooking } from "@/lib/booking";
import { Link } from "react-router-dom";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const { openBooking } = useBooking();

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen flex flex-col justify-center pt-32 pb-10 overflow-hidden">
      {/* Parallax oversized background word */}
      <motion.div
        style={{ y }}
        aria-hidden
        className="pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 select-none hidden md:block"
      >
        <span className="font-serif italic text-[22vw] leading-none text-[#F5F5F5]">Upword</span>
      </motion.div>

      <img
        src="/dandelion-mark.png"
        alt=""
        aria-hidden
        draggable={false}
        className="pointer-events-none select-none absolute -top-10 -right-14 w-52 md:-top-16 md:-right-20 md:w-[400px] opacity-[0.16]"
      />
      <motion.div style={{ opacity }} className="relative max-w-[1400px] mx-auto px-6 md:px-10 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="max-w-2xl text-xs md:text-sm uppercase tracking-[0.22em] text-[#666666] font-mono mb-8"
        >
          Upword Media Services | Engineered for SEO + GEO + AEO
        </motion.p>

        <h1 className="font-serif text-[7vw] sm:text-[2.4rem] lg:text-[3.2rem] xl:text-[3.75rem] leading-[0.98] tracking-tight text-[#111111]">
          <MaskedLines lines={["Organic Growth", "Partner for Leading"]} delay={0.2} />
          <span className="reveal-line">
            <motion.span
              className="block italic"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.2 + 2 * 0.12 }}
            >
              B2B SaaS &amp; Tech Brands.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.8 }}
          className="mt-8 max-w-2xl text-lg md:text-xl text-[#666666] leading-relaxed"
        >
          Content that ranks and gets cited by AI search, social and email that keep buyers warm,
          and conversion systems that turn traffic into pipeline.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.95 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            type="button"
            onClick={openBooking}
            data-testid="hero-primary-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#111111] text-white font-semibold px-8 py-4 hover:bg-[#333333] transition-colors group"
          >
            Schedule a Content Audit
            <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
          </button>
          <Link
            to="/contact"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 border border-[#111111] text-[#111111] font-semibold px-8 py-4 hover:bg-[#F5F5F5] transition-colors"
          >
            Talk to Us
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.15 }}
        className="relative mt-16 border-t border-[#E0E0E0] pt-8"
      >
        <ClientLogos />
      </motion.div>
    </section>
  );
};
